import { PERM } from "./permissions";

export const ROUTE_PERMISSIONS: Record<string, string> = {
  "/assist": PERM.VIEW_ASSISTANT,
  "/assist/chat": PERM.VIEW_ASSISTANT,
  "/assist/tutorial": PERM.VIEW_ASSISTANT,
  "/assist/personas": PERM.VIEW_ASSISTANT,
  "/assist/personas/org": PERM.VIEW_ASSISTANT,
  "/assist/objectives": PERM.VIEW_ASSISTANT,
  "/assist/context": PERM.VIEW_ASSISTANT,
  "/assist/agents": PERM.VIEW_ASSISTANT,
  "/assist/agents/schedules": PERM.VIEW_ASSISTANT,
  "/assist/workflows": PERM.VIEW_ASSISTANT,
  "/dashboard": PERM.VIEW_DASHBOARD,
  "/pulse/dls": PERM.VIEW_LABOR_STATS,
  "/pulse/hourly-report": PERM.VIEW_HOURLY_REPORT,
  "/price/rate-analysis": PERM.VIEW_RATE_ANALYSIS,
  "/price/rate-changes": PERM.VIEW_RATE_CHANGES,
  "/price/line-configs": PERM.VIEW_LINE_CONFIGS,
  "/track/work-orders": PERM.VIEW_WORK_ORDERS,
  "/track/work-orders/tracking": PERM.VIEW_WORK_ORDERS,
  "/ship/logistics": PERM.VIEW_LOGISTICS,
  "/ship/logistics/shippers": PERM.VIEW_SHIPPERS,
  "/track/customers": PERM.VIEW_CUSTOMERS,
  "/govern/reports": PERM.VIEW_REPORTS,
  "/govern/settings": PERM.VIEW_SETTINGS,
  "/maintain/machines": PERM.VIEW_MACHINES,
  "/maintain/machines/calls": PERM.VIEW_MACHINES,
  "/stock/pallets": PERM.VIEW_PALLETS,
  "/stock/materials": PERM.VIEW_MATERIALS,
  "/stock/parts": PERM.VIEW_PARTS,
  // The list moved into /stock/pallets#locations; this prefix now only gates
  // the /stock/locations/[id] detail pages (and the redirect off the bare path).
  "/stock/locations": PERM.VIEW_LOCATIONS,
  "/maintain/pms": PERM.VIEW_PMS,
  "/maintain/oee": PERM.VIEW_MACHINE_OEE,
  "/stock/mrp": PERM.VIEW_MRP,
  "/track/order-health": PERM.VIEW_SCHEDULE,
  "/pulse/aci-dls": PERM.VIEW_ACI_LABOR_STATS,
  "/pulse/unette-dls": PERM.VIEW_UNETTE_LABOR_STATS,
  "/pulse/corporate-dls": PERM.VIEW_CORPORATE_DLS,
  "/pulse/corporate-dls/top-products": PERM.VIEW_CORPORATE_DLS,
  "/track/pipeline": PERM.VIEW_PIPELINE,
  "/plan/scheduling": PERM.VIEW_SCHEDULE,
  // Capacity pages live under /plan/scheduling/<facility>/capacity and are
  // gated by ROUTE_PATTERN_PERMISSIONS below, since a static prefix table
  // cannot express "any facility slug" without an entry per facility.
  "/capacity/workforce": PERM.VIEW_CAPACITY,
  "/predict/forecast": PERM.VIEW_FORECAST,
  "/predict/forecast/worksheet": PERM.VIEW_FORECAST,
  "/predict/forecast/reconciliation": PERM.VIEW_FORECAST,
  "/quote/quote-assist": PERM.VIEW_QUOTES,
  "/quote/quote-assist/pending": PERM.APPROVE_QUOTES,
  "/quote/quote-assist/settings": PERM.MANAGE_QUOTE_RATES,
  "/quote/quote-assist/vocabularies": PERM.VIEW_QUOTES,
  "/govern/audit": PERM.VIEW_AUDIT,
  "/govern/employee": PERM.VIEW_EMPLOYEE,
  "/govern/report-card": PERM.VIEW_REPORT_CARD,
  "/account/budget": PERM.VIEW_BUDGET,
  "/account/accounts-payable": PERM.VIEW_AP,
  // The mail queue reads at VIEW_AP like the bill list: it is the same inbox
  // seen from a different angle. Clearing an item needs MANAGE_AP, enforced on
  // the route rather than here.
  "/account/accounts-payable/mail": PERM.VIEW_AP,
  "/account/accounts-payable/vendors": PERM.MANAGE_AP,
  "/account/accounts-payable/approvals": PERM.MANAGE_AP,
  "/account/accounts-payable/settings": PERM.MANAGE_AP,
  // The item catalog is built out of AP bill lines but has its own view
  // grant; MANAGE_AP (enforced per-route) is still needed to edit, map
  // supplier numbers, or merge duplicates.
  "/account/catalog": PERM.VIEW_AP_CATALOG,
  // "My approvals": a department approver's own queue. Its own grant so an
  // approver can be given this page and nothing else in AP. The API still
  // scopes results to bills mapped to the session user's email.
  "/account/approvals": PERM.VIEW_MY_APPROVALS,
  "/account/accounts-receivable": PERM.VIEW_AR,
  "/account/accounts-receivable/worklist": PERM.VIEW_AR,
  "/account/accounts-receivable/mapping": PERM.MANAGE_AR_MAP,
  "/account/ebitda": PERM.VIEW_EBITDA,
  "/account/labor-reconciliation": PERM.VIEW_LABOR_RECONCILIATION,
  "/account/production-reconciliation": PERM.VIEW_PRODUCTION_RECONCILIATION,
  "/account/inspection-reconciliation": PERM.VIEW_INSPECTION_RECONCILIATION,
  "/govern/admin": PERM.MANAGE_USERS,
  "/govern/admin/planning": PERM.MANAGE_USERS,
  "/govern/admin/ai": PERM.MANAGE_AI,
  "/govern/admin/connections": PERM.MANAGE_USERS,
  // Gated on its own permission rather than MANAGE_USERS: this screen decides
  // which Business Central company real invoices post into, which is a finance
  // decision, not a user-administration one.
  "/govern/admin/bc-companies": PERM.MANAGE_BC_ROUTING,
  "/govern/admin/errors": PERM.VIEW_ERRORS,
  "/govern/admin/health": PERM.MANAGE_USERS,
  "/govern/admin/cron": PERM.MANAGE_USERS,
  // Read-only: which release is live, what the validated baseline is, and how
  // far the cGMP scope has drifted from it. No actions on the page, so it sits
  // with the other platform-status screens rather than behind its own
  // permission. See docs/release/release-and-versioning.md.
  "/govern/admin/release": PERM.MANAGE_USERS,
  "/govern/admin/assumed-constants": PERM.MANAGE_CONSTANTS,
  "/govern/admin/module-savings": PERM.MANAGE_SAVINGS,
  "/govern/admin/security": PERM.MANAGE_SECURITY,
  // Regulate: cGMP quality record. Every route here is concept-stage; the
  // write actions (release a batch record, edit a quality record) are enforced
  // per-route on the API, not by the proxy, which only gates page access.
  "/regulate/batch-records": PERM.VIEW_BATCH_RECORDS,
  "/regulate/certifications": PERM.VIEW_CERTIFICATIONS,
  "/regulate/annual-product-review": PERM.VIEW_APR,
  "/regulate/mock-recall": PERM.VIEW_MOCK_RECALL,
  "/regulate/suppliers": PERM.VIEW_SUPPLIER_QUALITY,
  "/regulate/audits": PERM.VIEW_AUDIT_MGMT,
  "/regulate/etq": PERM.VIEW_ETQ,
  "/regulate/qms-map": PERM.VIEW_QMS_MAP,
  "/regulate/document-control": PERM.VIEW_DOCUMENT_CONTROL,
  "/regulate/stability": PERM.VIEW_STABILITY,
};

/**
 * Routes deliberately open to any authenticated user, with no permission gate.
 *
 * These are the exceptions to fail-closed nav filtering: every other href the
 * sidebar renders must resolve to a permission in ROUTE_PERMISSIONS or it is
 * hidden. Keep this list in sync with the equivalent allowlist in src/proxy.ts.
 */
export const OPEN_ROUTES: readonly string[] = [
  "/no-access",
  "/govern/account",
  // Deep Search scopes its results per-domain server-side, so the page itself
  // needs no single gate.
  "/search",
];

/**
 * Sentinel for an href that matches neither a mapped route nor an open route.
 * Callers must treat it as "no access". That is what keeps a newly added
 * module (a Regulate page, say) from leaking into the nav for everyone if its
 * ROUTE_PERMISSIONS entry is forgotten.
 */
export const UNMAPPED_ROUTE = "__unmapped__";

/**
 * Facility-scoped routes whose permission differs from their parent prefix.
 * /plan/scheduling/<slug> is VIEW_SCHEDULE, but the capacity page under it is
 * VIEW_CAPACITY for every facility, so it is matched as a pattern rather than
 * one static entry per slug. Patterns are checked before ROUTE_PERMISSIONS.
 * The slug group is deliberately permissive: an unknown slug 404s on the page
 * itself, and gating it under VIEW_CAPACITY is the fail-closed direction.
 */
export const ROUTE_PATTERN_PERMISSIONS: ReadonlyArray<{
  pattern: RegExp;
  perm: string;
}> = [
  { pattern: /^\/plan\/scheduling\/[^/]+\/capacity(?:\/|$)/, perm: PERM.VIEW_CAPACITY },
];

const SORTED_ROUTES = Object.entries(ROUTE_PERMISSIONS).sort(
  ([a], [b]) => b.length - a.length,
);

/**
 * Permission for a mapped route: patterns first, then longest static prefix.
 * Returns `undefined` when nothing matches. Shared by resolveRoutePermission
 * (sidebar) and src/proxy.ts (page access) so the two can never disagree.
 */
export function matchRoutePermission(path: string): string | undefined {
  for (const { pattern, perm } of ROUTE_PATTERN_PERMISSIONS) {
    if (pattern.test(path)) return perm;
  }
  for (const [route, perm] of SORTED_ROUTES) {
    if (path === route || path.startsWith(route + "/")) return perm;
  }
  return undefined;
}

/**
 * Resolve the permission that gates an href, mirroring the proxy's prefix
 * logic (src/proxy.ts) so facility-scoped hrefs like /plan/scheduling/<slug>
 * fall back to their parent route. Longest route wins, so a child route beats
 * its parent prefix. Returns `undefined` only for OPEN_ROUTES.
 */
export function resolveRoutePermission(href: string): string | undefined {
  // Hash-only children (`#preferences`) and query strings are not routes.
  const path = href.split("#")[0].split("?")[0];
  if (!path.startsWith("/")) return undefined;
  const perm = matchRoutePermission(path);
  if (perm !== undefined) return perm;
  if (OPEN_ROUTES.some((r) => path === r || path.startsWith(r + "/"))) {
    return undefined;
  }
  return UNMAPPED_ROUTE;
}

/** Top-level nav routes in sidebar order (used for login redirect fallback) */
const NAV_ROUTE_ORDER: string[] = [
  "/dashboard",
  "/assist",
  "/pulse/dls",
  "/pulse/hourly-report",
  "/price/rate-analysis",
  "/price/rate-changes",
  "/price/line-configs",
  "/track/work-orders",
  "/ship/logistics",
  "/ship/logistics/shippers",
  "/track/customers",
  "/maintain/machines",
  "/stock/pallets",
  "/stock/materials",
  "/stock/parts",
  "/maintain/pms",
  "/stock/mrp",
  "/track/order-health",
  "/pulse/aci-dls",
  "/pulse/unette-dls",
  "/pulse/corporate-dls",
  "/track/pipeline",
  "/plan/scheduling",
  "/quote/quote-assist",
  "/govern/audit",
  "/govern/employee",
  "/govern/report-card",
  "/account/budget",
  "/account/accounts-payable",
  // Listed right after the inbox so a user whose only AP grant is their own
  // approval queue lands there instead of on /no-access.
  "/account/approvals",
  "/account/catalog",
  "/account/accounts-receivable",
  "/account/ebitda",
  "/account/labor-reconciliation",
  "/account/production-reconciliation",
  "/account/inspection-reconciliation",
  "/regulate/batch-records",
  "/regulate/certifications",
  "/regulate/annual-product-review",
  "/regulate/mock-recall",
  "/regulate/suppliers",
  "/regulate/audits",
  "/regulate/etq",
  "/regulate/qms-map",
  "/regulate/document-control",
  "/regulate/stability",
  "/govern/reports",
  "/govern/settings",
  "/govern/admin",
];

/**
 * Return the first route the user can access, walking the sidebar in order.
 * Admins always land on /dashboard. Users with zero accessible routes get /no-access.
 */
export function getFirstAccessibleRoute(
  role: string,
  permissions: string[],
): string {
  if (role === "admin") return "/dashboard";

  for (const route of NAV_ROUTE_ORDER) {
    const requiredPerm = ROUTE_PERMISSIONS[route];
    if (!requiredPerm || permissions.includes(requiredPerm)) {
      return route;
    }
  }

  return "/no-access";
}
